import { useParticipants } from '@livekit/components-react';
import { useEffect, useRef, useState } from 'react';

import { APP_CONFIG } from '../config';
import { format } from '../i18n';
import { useStrings } from '../i18n/context';
import { isLanguageCode, languageInfo } from '../languages';
import { LANGUAGE_ATTRIBUTE } from '../protocol';
import { inviteUrl } from '../routing';
import { languageStatus } from '../status';
import { OpenInNewTab } from './OpenInNewTab';
import { PrivacyNotice } from './PrivacyNotice';

interface Props {
  readonly code: string;
  readonly embedded: boolean;
  readonly agents: ReadonlySet<string>;
}

function languageName(code: string): string {
  return isLanguageCode(code) ? languageInfo(code).nativeName : code;
}

export function MeetingHeader({ code, embedded, agents }: Props) {
  const strings = useStrings();
  const participants = useParticipants();
  const people = participants.filter((participant) => !participant.isAgent);
  const interpreterHere = participants.some((participant) => participant.isAgent && agents.has(participant.identity));
  const status = languageStatus(
    people.map((participant) => ({
      identity: participant.identity,
      name: participant.name || strings.someone,
      language: participant.attributes[LANGUAGE_ATTRIBUTE] ?? '',
      isLocal: participant.isLocal,
    })),
  );
  const [copyStatus, setCopyStatus] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);

  // Arriving in the room moves the focus here, so a screen reader announces where you are.
  useEffect(() => heading.current?.focus(), []);

  async function copyInvite() {
    const url = inviteUrl(code, APP_CONFIG.basePath, window.location.origin);
    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus(strings.inviteCopied);
    } catch {
      setCopyStatus(format(strings.inviteCopyFailed, { url }));
    }
  }

  const translationLine =
    status.translation === 'alone'
      ? strings.alone
      : status.translation === 'on'
        ? strings.translationOn
        : format(strings.translationNotNeeded, { language: languageName(status.languages[0] ?? '') });

  return (
    <header className="meeting__header">
      <h1 ref={heading} tabIndex={-1} className="meeting__title">
        <img className="brand__mark" src={`${APP_CONFIG.basePath}brand/logo-mark.svg`} alt="" width="26" height="24" />
        {strings.appTitle}
      </h1>
      <p className="meeting__code">{format(strings.meetingCode, { code })}</p>
      <p className="meeting__meta">
        <span>{format(strings.participantCount, { count: people.length })}</span>
        <span>{interpreterHere ? strings.interpreterPresent : strings.interpreterAbsent}</span>
      </p>
      {/* Who speaks what, and whether the interpreter has anything to translate. */}
      <p className="meeting__languages">
        {status.speakers.map((speaker) => (
          <span key={speaker.identity}>
            {format(strings.speaks, {
              name: speaker.isLocal ? format(strings.you, { name: speaker.name }) : speaker.name,
              language: languageName(speaker.language),
            })}
          </span>
        ))}
        <span className={status.translation === 'on' ? 'meeting__translation meeting__translation--on' : 'meeting__translation'}>
          {translationLine}
        </span>
      </p>
      <div className="meeting__actions">
        <button type="button" className="button" onClick={() => void copyInvite()}>
          {strings.copyInvite}
        </button>
        {embedded && <OpenInNewTab />}
      </div>
      <output className="meeting__status">{copyStatus}</output>
      <PrivacyNotice compact />
    </header>
  );
}
